import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/index.js';
import type { components } from '@/lib/api/schema.gen.js';
import { queryKeys } from '@/lib/query-client.js';

export type LoginRequest = {
  email: string;
  password: string;
  rememberDevice: boolean;
};

/**
 * Sign-in outcome.
 *
 * A discriminated union rather than "session or null", because correct
 * credentials do not always mean you are in — the password may have expired,
 * or a second factor may be required. Modelling those as branches means the
 * caller cannot forget to handle them.
 */
export type LoginResponse =
  | { status: 'AUTHENTICATED'; session: components['schemas']['Session'] }
  | {
      status: 'MFA_REQUIRED';
      challengeId: string;
      methods: string[];
      maskedDestination: string | null;
    }
  | { status: 'PASSWORD_EXPIRED'; challengeId: string; expiredAt: string };

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: LoginRequest) => api.post<LoginResponse>('/auth/login', body),
    onSuccess: (result) => {
      // Seed the session cache directly rather than refetching. The server has
      // already told us who we are, and a second round trip would show a
      // loading state for data we are holding.
      if (result.status === 'AUTHENTICATED') {
        queryClient.setQueryData(queryKeys.session, result.session);
      }
    },
  });
}
